import { Button, Col, DatePicker, Flex, Form, Input, Modal, Row, Select, Tag } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notifyError } from '../store/utils';
import { API } from '../api';
import dayjs from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { parseSearchParam } from '../store/orderUtils';
import { UserContext } from '../context/UserProvider';
import { deliveryTypes } from '../store/StoreUtils';

const itemLayout = {
  labelCol: {
    md: { span: 6 },
  },
  wrapperCol: {
    md: { span: 15 },
  },
};

const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';

interface OrderSearchProps {
  isCustomer?: boolean;
  isScanBranch?: boolean;
  isLastBranch?: boolean;
  isDeliveryMan?: boolean;
  isPrint?: boolean;
  onSearch: (params: any) => void;
}

const OrderSearch = React.memo(
  ({
    isCustomer = true,
    isScanBranch = true,
    isLastBranch = true,
    isDeliveryMan = true,
    isPrint = true,
    onSearch,
  }: OrderSearchProps) => {
    const { getCustomer } = useContext(UserContext);
    const location = useLocation();
    const [form] = Form.useForm();
    const [form1] = Form.useForm();
    const [visible, updateVisible] = useState(false);
    const [visible1, updateVisible1] = useState(false);
    const [customers, updateCustomers] = useState([]);
    const [scanBranchs, updateScanBranchs] = useState([]);
    const [lastBranchs, updateLastBranchs] = useState([]);
    const [deliveryMans, updateDeliveryMans] = useState([]);
    const [channels, updateChannels] = useState([]);
    const [provinces, updateProvinces] = useState([]);
    const [districts, updatedDstricts] = useState([]);

    const search = useCallback(
      (query: any) => {
        const payload = parseSearchParam(query);
        onSearch(payload);
      },
      [location]
    );

    const simpleSearch = useCallback(
      (value: string) => {
        const params = queryString.parse(location.search);
        Object.keys(params).forEach((key: string) => {
          params[key] = '';
        });
        params.q = value;
        search(params);
      },
      [location]
    );

    const setValues = useCallback(() => {
      const params = queryString.parse(location.search);
      form.setFieldsValue({
        orderNo: params?.orderNo || undefined,
        barcode: params?.barcode || undefined,
        isPrint: params?.isPrint || undefined,
        deliveryType: params?.deliveryType || undefined,
        orderDate:
          params?.orderStartDate && params.orderEndDate
            ? [dayjs(params?.orderStartDate as string), dayjs(params.orderEndDate as string)]
            : [null, null],
        scanDate:
          params?.scanStartDate && params.scanEndDate
            ? [dayjs(params?.scanStartDate as string), dayjs(params.scanEndDate as string)]
            : [null, null],
        senderQ: params?.senderQ || undefined,
        receiverQ: params?.receiverQ || undefined,
        scanBranchNo: params?.scanBranchNo ? parseInt(params?.scanBranchNo as string) : undefined,
        lastBranchNo: params?.lastBranchNo ? parseInt(params?.lastBranchNo as string) : undefined,
        deliveryManNo: params?.deliveryManNo
          ? parseInt(params?.deliveryManNo as string)
          : undefined,
        customerNo: params?.customerNo ? parseInt(params?.customerNo as string) : undefined,
        districtNo: params?.districtNo ? parseInt(params?.districtNo as string) : undefined,
        svc: params?.svc || undefined,
      });
    }, [location, form1, form]);

    const setValues1 = useCallback(() => {
      const params = queryString.parse(location.search);
      form1.setFieldsValue({
        provinceNo: params?.provinceNo ? parseInt(params?.provinceNo as string) : undefined,
        districtNo: params?.districtNo ? parseInt(params?.districtNo as string) : undefined,
      });
    }, [location, form1]);

    const fetchChannelTypes = useCallback(async () => {
      try {
        const { result } = await API.get({ apiVersion: 'api' })(`/settings/channels`);
        const list = result || [];
        updateChannels(list);
      } catch (error: any) {
        notifyError(error.message);
      }
    }, []);

    const fetchCustomer = useCallback(
      async (query?: string) => {
        try {
          const params = queryString.parse(location.search);
          let url = `/customer/list?page=1&size=10`;
          if (params.customerNo) {
            url = `/customer/list?page=1&size=10&required=${params.customerNo}`;
          }
          if (query) {
            url = `/customer/list?page=1&size=10&query=${query}`;
          }
          const { result } = await API.get({ apiVersion: 'api' })(url);
          updateCustomers(result?.data || []);
        } catch (error: any) {
          notifyError(error.message);
        }
      },
      [location]
    );

    const fetchScanBranchs = useCallback(
      async (query?: string) => {
        try {
          const params = queryString.parse(location.search);
          let url = `/branch/list1?page=1&size=10`;
          if (params.scanBranchNo) {
            url = `/branch/list1?page=1&size=10&required=${params.scanBranchNo}`;
          }
          if (query) {
            url = `/branch/list1?page=1&size=10&query=${query}`;
          }
          const { result } = await API.get({ apiVersion: 'api' })(url);
          updateScanBranchs(result?.data);
        } catch (error: any) {
          notifyError(error.message);
        }
      },
      [location]
    );

    const fetchLastBranchs = useCallback(
      async (query?: string) => {
        try {
          const params = queryString.parse(location.search);
          let url = `/branch/list1?page=1&size=10`;
          if (params.lastBranchNo) {
            url = `/branch/list1?page=1&size=10&required=${params.lastBranchNo}`;
          }
          if (query) {
            url = `/branch/list1?page=1&size=10&query=${query}`;
          }
          const { result } = await API.get({ apiVersion: 'api' })(url);
          updateLastBranchs(result?.data);
        } catch (error: any) {
          notifyError(error.message);
        }
      },
      [location]
    );

    const fetchDeliveryMans = useCallback(
      async (query?: string) => {
        try {
          const params = queryString.parse(location.search);
          let url = `/member/branch/delivery-man?page=1&size=10&logistic=true`;
          if (params.lastBranchNo) {
            url = `/member/branch/delivery-man?page=1&size=10&logistic=true&required=${params.lastBranchNo}`;
          }
          if (query) {
            url = `/member/branch/delivery-man?page=1&size=10&logistic=true&query=${query}`;
          }
          const { result } = await API.get({ apiVersion: 'api' })(url);
          updateDeliveryMans(result?.data);
        } catch (error: any) {
          notifyError(error.message);
        }
      },
      [location]
    );

    const fetchProvinces = useCallback(async () => {
      try {
        const params = queryString.parse(location.search);
        let url = `/address/province/list`;
        const { result } = await API.get({ apiVersion: 'api' })(url);
        updateProvinces(result?.data || []);
      } catch (error: any) {
        notifyError(error.message);
      }
    }, [location]);

    const fetchDistricts = useCallback(async (provinceNo: string) => {
      try {
        const { result } = await API.get({ apiVersion: 'api' })(`/address/districts/${provinceNo}`);
        updatedDstricts(result?.data || []);
      } catch (error: any) {
        notifyError(error.message);
      }
    }, []);

    const onFinish = useCallback(
      (values: any) => {
        const params = queryString.parse(location.search);
        const payload = {
          ...params,
          ...values,
          orderStartDate: values?.orderDate?.[0]
            ? dayjs(values?.orderDate?.[0]).startOf('day').toISOString()
            : '',
          orderEndDate: values?.orderDate?.[1]
            ? dayjs(values?.orderDate?.[1]).endOf('day').toISOString()
            : '',
          scanStartDate: values?.scanDate?.[0]
            ? dayjs(values?.scanDate?.[0]).startOf('day').toISOString()
            : '',
          scanEndDate: values?.scanDate?.[1]
            ? dayjs(values?.scanDate?.[1]).endOf('day').toISOString()
            : '',
        };
        updateVisible(false);
        search(payload);
      },
      [location]
    );

    const onFinish1 = useCallback(
      (values: any) => {
        const params = queryString.parse(location.search);
        const payload = {
          ...params,
          ...values,
          isPrint: false,
          page: 1,
          size: 10,
        };
        updateVisible1(false);
        search(payload);
      },
      [location]
    );

    const clearIsprint = useCallback(() => {
      const params = queryString.parse(location.search);
      const payload = {
        ...params,
        isPrint: undefined,
        provinceNo: undefined,
        districtNo: undefined,
        page: 1,
        size: 10,
      };
      updateVisible1(false);
      search(payload);
    }, [location]);

    useEffect(() => {
      fetchChannelTypes();
    }, []);

    useEffect(() => {
      if (visible) setValues();
    }, [location, visible]);

    useEffect(() => {
      if (visible1) setValues1();
    }, [location, visible1]);

    useEffect(() => {
      if (isCustomer) {
        fetchCustomer();
      }
    }, [isCustomer]);

    useEffect(() => {
      if (isScanBranch) {
        fetchScanBranchs();
      }
    }, [isScanBranch]);

    useEffect(() => {
      if (isLastBranch) {
        fetchLastBranchs();
      }
    }, [isLastBranch]);

    useEffect(() => {
      if (isDeliveryMan) {
        fetchDeliveryMans();
      }
    }, [isDeliveryMan]);

    useEffect(() => {
      if (isPrint) {
        fetchProvinces();
      }
    }, [isPrint]);

    useEffect(() => {
      if (!visible1 && isPrint) {
        const params = queryString.parse(location.search);
        if (params.provinceNo) {
          fetchDistricts(params.provinceNo as string);
        }
      }
    }, [fetchDistricts, isPrint, location, visible1]);

    const customerOptions = useMemo(() => {
      return customers.map((item: any) => {
        return {
          label: item.name,
          value: item.no,
        };
      });
    }, [customers]);

    const scanBranchOptions = useMemo(() => {
      return scanBranchs.map((item: any) => {
        return {
          label: item.name,
          value: item.no,
        };
      });
    }, [scanBranchs]);

    const lastBranchOptions = useMemo(() => {
      return lastBranchs.map((item: any) => {
        return {
          label: item.name,
          value: item.no,
        };
      });
    }, [lastBranchs]);

    const deliveryManOptions = useMemo(() => {
      return deliveryMans.map((item: any) => {
        return {
          label: item.firstName,
          value: item.no,
        };
      });
    }, [deliveryMans]);

    const channelOptions = useMemo(() => {
      return channels.map((item: any) => {
        return {
          label: item.text,
          value: item.code,
        };
      });
    }, [channels]);

    const provinceOptions = useMemo(() => {
      return provinces.map((item: any) => {
        return {
          label: item.name,
          value: item.no,
        };
      });
    }, [provinces]);

    const districtOptions = useMemo(() => {
      return districts.map((item: any) => {
        return {
          label: item.name,
          value: item.no,
        };
      });
    }, [districts]);

    const searchParams = useMemo(() => {
      const searchParam = queryString.parse(location.search);
      const list: any = [];
      if (searchParam.orderNo) {
        list.push({ type: 'Захиалгын #', name: searchParam.orderNo, field: 'orderNo' });
      }
      if (searchParam.barcode) {
        list.push({ type: 'Баримтын #', name: searchParam.barcode, field: 'barcode' });
      }
      if (searchParam.orderStartDate && searchParam.orderEndDate) {
        list.push({
          type: 'Захиалгын огноо',
          name:
            dayjs(searchParam.orderStartDate as string).format(dateFormat) +
            '~' +
            dayjs(searchParam.orderEndDate as string).format(dateFormat),
          field: 'orderDate',
        });
      }
      if (searchParam.scanStartDate && searchParam.scanEndDate) {
        list.push({
          type: 'Скан огноо',
          name:
            dayjs(searchParam.scanStartDate as string).format(dateFormat) +
            '~' +
            dayjs(searchParam.scanEndDate as string).format(dateFormat),
          field: 'scanDate',
        });
      }
      if (searchParam.deliveryType) {
        const delivery = deliveryTypes.find((ty: any) => ty.value === searchParam.deliveryType);
        if (delivery) {
          list.push({ type: 'Хүргэлтийн төрөл', name: delivery.label, field: 'deliveryType' });
        }
      }
      if (searchParam.senderQ) {
        list.push({ type: 'Илгээгч', name: searchParam.senderQ, field: 'senderQ' });
      }
      if (searchParam.receiverQ) {
        list.push({ type: 'Хүлээн авагч', name: searchParam.receiverQ, field: 'receiverQ' });
      }
      if (searchParam.customerNo) {
        let customer: any = null;
        customers.forEach((item: any) => {
          if (item.no == searchParam.customerNo) {
            customer = item;
          }
        });
        if (customer) {
          list.push({ type: 'Харилцагч', name: customer.name, field: 'customerNo' });
        }
      }
      if (searchParam.lastBranchNo) {
        let lastBranch: any = null;
        lastBranchs.forEach((item: any) => {
          if (item.no == searchParam.lastBranchNo) {
            lastBranch = item;
          }
        });
        if (lastBranch) {
          list.push({ type: 'ХА.Салбар', name: lastBranch.name, field: 'lastBranchNo' });
        }
      }
      if (searchParam.scanBranchNo) {
        let lastBranch: any = null;
        scanBranchs.forEach((item: any) => {
          if (item.no == searchParam.scanBranchNo) {
            lastBranch = item;
          }
        });
        if (lastBranch) {
          list.push({ type: 'Скан салбар', name: lastBranch.name, field: 'scanBranchNo' });
        }
      }
      if (searchParam.deliveryManNo) {
        let deliveryMan: any = null;
        deliveryMans.forEach((item: any) => {
          if (item.no == searchParam.deliveryManNo) {
            deliveryMan = item;
          }
        });
        if (deliveryMan) {
          list.push({ type: 'Хүргэгч', name: deliveryMan.fname, field: 'deliveryManNo' });
        }
      }
      if (searchParam.provinceNo && searchParam.districtNo) {
        const province: any = provinces.find(
          (it: any) => it.no === parseInt(searchParam.provinceNo as string)
        );
        const district: any = districts.find(
          (it: any) => it.no === parseInt(searchParam.districtNo as string)
        );
        if (province && district) {
          list.push({ type: 'Хаяг', name: `${province.name} ${district.name}`, field: 'isPrint' });
        }
      }
      if (searchParam.svc) {
        list.push({ type: 'Суваг', name: searchParam.svc, field: 'svc' });
      }
      return list;
    }, [customers, lastBranchs, deliveryMans, scanBranchs, location, provinces, districts]);

    const removeFilter = useCallback(
      (field: string) => {
        const params: any = queryString.parse(location.search);
        let payload = {
          ...params,
        };
        if (field === 'orderDate') {
          payload = {
            ...payload,
            orderStartDate: undefined,
            orderEndDate: undefined,
          };
        } else if (field === 'scanDate') {
          payload = {
            ...payload,
            scanStartDate: undefined,
            scanEndDate: undefined,
          };
        } else if (field === 'isPrint') {
          payload = {
            ...payload,
            provinceNo: undefined,
            districtNo: undefined,
          };
        } else {
          payload[field] = undefined;
        }
        search(payload);
      },
      [form, form1, location]
    );

    return (
      <div>
        <Row gutter={[24, 24]}>
          <Col span={10}>
            <Input.Search
              placeholder="Захиалгын #, Баримтын #, утас, нэр"
              enterButton
              onSearch={(value: string) => simpleSearch(value)}
            />
          </Col>
          <Col span={6}>
            <Flex gap={24}>
              <Button onClick={() => updateVisible(true)}>Нэмэлт Хайлт</Button>
              {isPrint ? (
                <Button
                  type="default"
                  icon={<SearchOutlined />}
                  onClick={() => updateVisible1(true)}
                >
                  баримт хэвлээгүй
                </Button>
              ) : null}
            </Flex>
          </Col>
          <Col span={24}>
            <Flex gap={6}>
              {searchParams.map((item: any) => {
                return (
                  <Tag
                    key={`item_index_${item.field}`}
                    color="blue"
                    closable={true}
                    onClose={() => removeFilter(item.field)}
                  >
                    {item.type}: {item.name}
                  </Tag>
                );
              })}
            </Flex>
          </Col>
        </Row>
        <Modal
          title="Нэмэлт Хайлт"
          footer={false}
          open={visible}
          onCancel={() => updateVisible(false)}
        >
          <Form layout="horizontal" form={form} onFinish={onFinish}>
            <Form.Item label="Суваг" {...itemLayout} name="svc">
              <Select options={channelOptions} allowClear />
            </Form.Item>
            <Form.Item label="Захиалгын дугаар" name="orderNo" {...itemLayout}>
              <Input placeholder="Захиалга" />
            </Form.Item>
            <Form.Item label="Баримтын дугаар" name="barcode" {...itemLayout}>
              <Input placeholder="Баримт" />
            </Form.Item>
            <Form.Item label="Захиалсан огноо" name="orderDate" {...itemLayout}>
              <RangePicker style={{ width: '100%' }} value={[null, null]} format={dateFormat} />
            </Form.Item>
            <Form.Item label="Скан огноо" name="scanDate" {...itemLayout}>
              <RangePicker style={{ width: '100%' }} value={[null, null]} format={dateFormat} />
            </Form.Item>
            <Form.Item label="Хүргэлтийн төрөл" name="deliveryType" {...itemLayout}>
              <Select options={deliveryTypes} allowClear />
            </Form.Item>
            {isCustomer ? (
              <Form.Item name="customerNo" label="Харилцагч" {...itemLayout}>
                <Select
                  options={customerOptions}
                  showSearch
                  allowClear
                  onSearch={(query: string) => fetchCustomer(query)}
                  filterOption={false}
                />
              </Form.Item>
            ) : null}
            <Form.Item label="Хүлээн авагч" {...itemLayout} name="receiverQ">
              <Input />
            </Form.Item>
            {isScanBranch ? (
              <Form.Item label="Скан салбар" {...itemLayout} name="scanBranchNo">
                <Select
                  options={scanBranchOptions}
                  showSearch
                  allowClear
                  onSearch={(query: string) => fetchScanBranchs(query)}
                  filterOption={false}
                />
              </Form.Item>
            ) : null}
            {isLastBranch ? (
              <Form.Item label="Авах салбар" {...itemLayout} name="lastBranchNo">
                <Select
                  options={lastBranchOptions}
                  showSearch
                  allowClear
                  onSearch={(query: string) => fetchLastBranchs(query)}
                  filterOption={false}
                />
              </Form.Item>
            ) : null}
            {isDeliveryMan ? (
              <Form.Item label="Скан хүргэгч" {...itemLayout} name="deliveryManNo">
                <Select
                  options={deliveryManOptions}
                  showSearch
                  allowClear
                  onSearch={(query: string) => fetchDeliveryMans(query)}
                  filterOption={false}
                />
              </Form.Item>
            ) : null}
            <Form.Item>
              <Flex style={{ marginRight: 60 }} justify="end" gap={24}>
                <Button onClick={() => updateVisible(false)} htmlType="button">
                  Цуцлах
                </Button>
                <Button type="primary" htmlType="submit">
                  Хайх
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Баримт хэвлээгүй"
          footer={false}
          open={visible1}
          onCancel={() => updateVisible1(false)}
        >
          <Form layout="horizontal" form={form1} onFinish={onFinish1}>
            <Form.Item name="provinceNo">
              <Select
                options={provinceOptions}
                placeholder="Аймаг хот"
                showSearch
                allowClear
                onSelect={(value) => fetchDistricts(value)}
                filterOption={false}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.provinceNo !== currentValues.provinceNo
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('provinceNo') ? (
                  <Form.Item name="districtNo">
                    <Select
                      placeholder="Сум дүүрэг"
                      options={districtOptions}
                      showSearch
                      allowClear
                      filterOption={false}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item>
                    <Select disabled placeholder="Сум дүүрэг" allowClear />
                  </Form.Item>
                )
              }
            </Form.Item>
            <Form.Item>
              <Flex justify="end" gap={24}>
                <Button onClick={() => clearIsprint()} htmlType="button">
                  Цуцлах
                </Button>
                <Button type="primary" htmlType="submit">
                  Хайх
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
);

export default OrderSearch;
